/**
 * React Query hooks for stock class feed allocations.
 * React Query hooks for the corresponding entity.
 */

import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";

export enum FEED_ALLOCATION_TYPE {
  STOCK_CLASS = "STOCK_CLASS",
  OFFSPRING = "OFFSPRING",
}

export interface StockClassFeedAllocation {
  guid: string;
  dateStart: string;
  allocatedBodyWeightFraction: number;
  allocationType: FEED_ALLOCATION_TYPE;
  stockClassGuid: string;
}

const stockClassFeedAllocationApi = new API<StockClassFeedAllocation>(
  "stockClassFeedAllocations"
);

/**
 * Fetch feed allocations by their IDs
 */
export function useFeedAllocationsByIds(
  feedAllocationIds: string[] | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: ["feedAllocations", "byIds", feedAllocationIds?.sort().join(",")],
    queryFn: async () => {
      if (!feedAllocationIds || feedAllocationIds.length === 0) {
        return [];
      }
      return await stockClassFeedAllocationApi.getMany(feedAllocationIds);
    },
    enabled: enabled && !!feedAllocationIds && feedAllocationIds.length > 0,
  });
}

/**
 * Create a feed allocation
 */
export function useCreateFeedAllocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedAllocation: StockClassFeedAllocation) => {
      const result = await stockClassFeedAllocationApi.createOne(
        feedAllocation
      );
      return await stockClassFeedAllocationApi.getOne(result.guid);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.feedAllocations.byStockClass(data.stockClassGuid),
        (old: StockClassFeedAllocation[] | undefined) => [...(old || []), data]
      );
    },
  });
}

/**
 * Update a feed allocation
 */
export function useUpdateFeedAllocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedAllocation: StockClassFeedAllocation) => {
      await stockClassFeedAllocationApi.updateOne(feedAllocation);
      return await stockClassFeedAllocationApi.getOne(feedAllocation.guid);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.feedAllocations.byStockClass(data.stockClassGuid),
        (old: StockClassFeedAllocation[] | undefined) =>
          (old || []).map((fa) => (fa.guid === data.guid ? data : fa))
      );
    },
  });
}

/**
 * Delete a feed allocation
 */
export function useDeleteFeedAllocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      feedAllocationId: string;
      stockClassGuid: string;
    }) => {
      await stockClassFeedAllocationApi.deleteOne(params.feedAllocationId);
      return params;
    },
    onSuccess: ({ feedAllocationId, stockClassGuid }) => {
      queryClient.setQueryData(
        queryKeys.feedAllocations.byStockClass(stockClassGuid),
        (old: StockClassFeedAllocation[] | undefined) =>
          (old || []).filter((fa) => fa.guid !== feedAllocationId)
      );
    },
  });
}

/**
 * Prefetch feed allocations by their IDs (used during initialization)
 */
export async function fetchFeedAllocationsByIdsQuery(
  feedAllocationIds: string[],
  queryClient: QueryClient
): Promise<StockClassFeedAllocation[]> {
  if (feedAllocationIds.length === 0) return [];

  const allocations = await stockClassFeedAllocationApi.getMany(
    feedAllocationIds
  );

  // Group by stock class and cache
  const byStockClass: Record<string, StockClassFeedAllocation[]> = {};
  allocations.forEach((fa) => {
    if (!byStockClass[fa.stockClassGuid]) {
      byStockClass[fa.stockClassGuid] = [];
    }
    byStockClass[fa.stockClassGuid].push(fa);
  });

  Object.entries(byStockClass).forEach(([stockClassGuid, allocations]) => {
    queryClient.setQueryData(
      queryKeys.feedAllocations.byStockClass(stockClassGuid),
      allocations
    );
  });

  return allocations;
}

/**
 * Reset feed allocations query cache
 */
export function resetFeedAllocationsQueryCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: queryKeys.feedAllocations.all });
}
